import { queryOptions } from '@tanstack/react-query';

import { SensorTypeCode } from '@/common/asset-sensor';
import type {
	MetaRecord,
	TpsSensorAlarmRecord,
	TpsSensorTimeSeriesChartRecord,
} from '@/common/sensors/tps/tps-timeseries-chart';
import { axios } from '@/lib/utils/axios';
import { getResponse } from '@/queries/common';
import { API_URLS } from '@/queries/query.client';
import { ROLE } from '../../../../common/role';
import { usePermission } from '../../../../hooks/use-permission';

export type TpsSensorTimeSeriesChartResponse = {
	chartData: TpsSensorTimeSeriesChartRecord[];
	alarms: TpsSensorAlarmRecord;
	meta: MetaRecord;
};

export type TpsSensorTimeSeriesChartFilters = {
	from?: string;
	to?: string;
	geglattet?: boolean;
	delta?: boolean;
	aggregation?: 'hourly' | 'daily' | 'monthly';
};

async function getTpsTimeSeriesChart(
	assetId: string,
	dataLoggerId: number,
	sensorId: number,
	query: TpsSensorTimeSeriesChartFilters,
): Promise<TpsSensorTimeSeriesChartResponse | null> {
	const response = await axios.get(`${API_URLS.ASSETS.SENSORS.TPS.TIMESERIES(assetId, dataLoggerId, sensorId)}`, {
		params: query,
	});
	return getResponse<TpsSensorTimeSeriesChartResponse>(response.data);
}

export const useQueryGetTpsTimeSeriesChart = (
	assetId: string,
	dataLoggerId: number,
	sensorId: number,
	filters: TpsSensorTimeSeriesChartFilters,
	enabled = true,
) => {
	const shouldRead = usePermission(ROLE.SENSORS_TPS_TIMESERIES_READ);
	return queryOptions({
		queryKey: ['assets', assetId, 'sensors', SensorTypeCode.TPS, dataLoggerId, sensorId, 'timeseries', filters],
		queryFn: () => getTpsTimeSeriesChart(assetId, dataLoggerId, sensorId, filters),
		enabled: enabled && !!assetId && !!sensorId && !!filters.from && !!filters.to && shouldRead,
	});
};
